// Security settings — currently just the trusted-devices manager. We
// keep the page standalone (not nested into the per-role profile pages)
// so the surface is the same regardless of whether the caller is a
// Club/Agent/Coach/Staff. The PrivateRoute wrapping the route handles
// auth; this page assumes the user is signed in.
//
// Layout mirrors the existing white-on-#F6F8FB look used elsewhere
// (compare Profile pages). Primary accent #2283FF.
//
// What this page renders:
//   - A "Trusted devices" card with one row per active TrustedDevice
//   - Each row: device name + IP + created/last-used/expires, plus a
//     "This device" badge when the row matches our current cookie
//   - A "Revoke" button per row + a "Revoke all devices" button (with
//     confirm) below the list
//   - Empty state when no trusted devices exist
//
// Errors are surfaced via toast (consistent with the rest of the app).

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import toast from "react-hot-toast";
import { trustedDeviceService } from "../../services/api";

const ACCENT = "#2283FF";
const BORDER = "#E5E7EB";

// ---------------------------------------------------------------------------
// Pretty-printers — kept local so the page doesn't pull in a date lib just
// for this one screen. Falls back gracefully on malformed input.
// ---------------------------------------------------------------------------

const LONG_DATE = new Intl.DateTimeFormat("en-US", {
  month: "short", day: "numeric", year: "numeric",
});
const TIME_FMT = new Intl.DateTimeFormat("en-US", {
  hour: "2-digit", minute: "2-digit",
});

function formatDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "—";
  return LONG_DATE.format(d);
}

function formatRelativeFromNow(iso) {
  if (!iso) return "—";
  const target = new Date(iso);
  if (isNaN(target.getTime())) return "—";
  const diffMs = target.getTime() - Date.now();
  if (diffMs <= 0) return "expired";
  const days = Math.floor(diffMs / 86_400_000);
  if (days >= 1) return `in ${days} day${days === 1 ? "" : "s"}`;
  const hours = Math.floor(diffMs / 3_600_000);
  if (hours >= 1) return `in ${hours} hour${hours === 1 ? "" : "s"}`;
  return "in <1 hour";
}

function formatLastUsed(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "—";
  const now = new Date();
  const isToday = d.toDateString() === now.toDateString();
  if (isToday) return `Today at ${TIME_FMT.format(d)}`;
  const yesterday = new Date(now.getTime() - 86_400_000);
  if (d.toDateString() === yesterday.toDateString()) {
    return `Yesterday at ${TIME_FMT.format(d)}`;
  }
  return LONG_DATE.format(d);
}

// Mask the last octet of an IPv4 for display privacy. IPv6 isn't masked
// (the address space is huge, less correlation risk in casual display).
function maskIp(ip) {
  if (!ip) return "—";
  if (ip.includes(".")) {
    const parts = ip.split(".");
    if (parts.length === 4) return `${parts[0]}.${parts[1]}.${parts[2]}.x`;
  }
  return ip;
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

const SecuritySettings = () => {
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [revoking, setRevoking] = useState(null); // device id or 'all'
  const [confirmRevokeAll, setConfirmRevokeAll] = useState(false);
  const abortRef = useRef(null);

  const fetchDevices = useCallback(async () => {
    if (abortRef.current) abortRef.current.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    setLoading(true);
    setError(null);
    try {
      const res = await trustedDeviceService.list({ signal: ctrl.signal });
      if (ctrl.signal.aborted) return;
      if (res.success) {
        setDevices(Array.isArray(res.data) ? res.data : []);
      } else {
        setError(res.error || "Failed to load trusted devices.");
      }
    } catch (e) {
      if (e?.name !== "CanceledError") {
        setError("Failed to load trusted devices.");
      }
    } finally {
      if (!ctrl.signal.aborted) setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDevices();
    return () => abortRef.current?.abort();
  }, [fetchDevices]);

  const handleRevoke = async (device) => {
    if (revoking) return;
    setRevoking(device.id);
    const res = await trustedDeviceService.revoke(device.id);
    if (res.success) {
      toast.success(
        device.is_current_device
          ? "This device revoked. You'll need OTP on the next login."
          : `Revoked ${device.device_name || "device"}.`,
      );
      setDevices((prev) => prev.filter((d) => d.id !== device.id));
    } else {
      toast.error(res.error || "Failed to revoke device.");
    }
    setRevoking(null);
  };

  const handleRevokeAll = async () => {
    if (revoking) return;
    setRevoking("all");
    const res = await trustedDeviceService.revokeAll();
    if (res.success) {
      toast.success(
        `${res.data?.revoked_count ?? "All"} device(s) revoked. You'll need OTP on the next login.`,
      );
      setDevices([]);
      setConfirmRevokeAll(false);
    } else {
      toast.error(res.error || "Failed to revoke all devices.");
    }
    setRevoking(null);
  };

  const hasDevices = devices.length > 0;
  const currentDeviceLabel = useMemo(
    () => devices.find((d) => d.is_current_device)?.device_name || null,
    [devices],
  );

  return (
    <div className="min-h-screen bg-[#F6F8FB] py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl mx-auto">
        <header className="mb-6">
          <h1 className="text-2xl font-bold text-[#0F172A]">Security</h1>
          <p className="text-sm text-[#475569] mt-1">
            Manage which devices can sign in without an OTP code.
          </p>
        </header>

        <section
          className="bg-white rounded-xl shadow-sm border"
          style={{ borderColor: BORDER, borderRadius: 12 }}
        >
          <div className="px-5 py-4 border-b" style={{ borderColor: BORDER }}>
            <h2 className="text-lg font-semibold text-[#0F172A]">Trusted devices</h2>
            <p className="text-sm text-[#475569] mt-0.5">
              Devices you marked as trusted skip the OTP step for 14 days.
              Revoke any device you no longer recognise.
            </p>
            {currentDeviceLabel && (
              <p className="text-xs text-[#94A3B8] mt-1">
                Current browser: <span className="font-medium text-[#475569]">{currentDeviceLabel}</span>
              </p>
            )}
          </div>

          {loading && (
            <div className="p-8 text-center text-[#94A3B8] text-sm">
              Loading devices…
            </div>
          )}

          {!loading && error && (
            <div className="p-8 text-center text-[#EF4444] text-sm">
              {error}
              <div className="mt-2">
                <button
                  type="button"
                  onClick={fetchDevices}
                  className="text-[#2283FF] hover:underline"
                >
                  Retry
                </button>
              </div>
            </div>
          )}

          {!loading && !error && !hasDevices && (
            <div className="p-8 text-center text-[#94A3B8] text-sm">
              <div className="text-base text-[#475569] mb-1">No trusted devices</div>
              You haven't marked any browser as trusted yet. Next time you sign
              in, tick "Trust this device for 14 days" to skip OTP on this
              browser.
            </div>
          )}

          {!loading && !error && hasDevices && (
            <ul className="divide-y" style={{ borderColor: BORDER }}>
              {devices.map((d) => (
                <li key={d.id} className="px-5 py-4 flex items-start gap-4">
                  <div
                    className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                    style={{ background: "#EBF5FF", color: ACCENT }}
                    aria-hidden="true"
                  >
                    {/* Generic device glyph; no per-browser icon to avoid
                        leaking another network roundtrip. */}
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                      <rect x="3" y="5" width="18" height="12" rx="2"
                        stroke="currentColor" strokeWidth="1.8" />
                      <path d="M8 21h8M12 17v4" stroke="currentColor"
                        strokeWidth="1.8" strokeLinecap="round" />
                    </svg>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-[#0F172A] truncate">
                        {d.device_name || "Unknown device"}
                      </span>
                      {d.is_current_device && (
                        <span
                          className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold"
                          style={{ background: "#EBF5FF", color: ACCENT }}
                        >
                          This device
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-[#475569] mt-1 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-0.5">
                      <span>IP: <span className="font-medium text-[#0F172A]">{maskIp(d.ip_address)}</span></span>
                      <span>Created: <span className="font-medium text-[#0F172A]">{formatDate(d.created_at)}</span></span>
                      <span>Last used: <span className="font-medium text-[#0F172A]">{formatLastUsed(d.last_used_at)}</span></span>
                      <span>Expires: <span className="font-medium text-[#0F172A]">{formatRelativeFromNow(d.trusted_until)}</span></span>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleRevoke(d)}
                    disabled={revoking !== null}
                    className="text-sm font-semibold text-[#EF4444] hover:bg-[#FEF2F2] px-3 py-1.5 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {revoking === d.id ? "Revoking…" : "Revoke"}
                  </button>
                </li>
              ))}
            </ul>
          )}

          {!loading && hasDevices && (
            <div className="px-5 py-4 border-t flex items-center justify-between" style={{ borderColor: BORDER }}>
              <span className="text-xs text-[#94A3B8]">
                {devices.length} active device{devices.length === 1 ? "" : "s"}
              </span>
              {!confirmRevokeAll ? (
                <button
                  type="button"
                  onClick={() => setConfirmRevokeAll(true)}
                  className="text-sm font-semibold text-[#EF4444] hover:underline"
                  disabled={revoking !== null}
                >
                  Revoke all devices
                </button>
              ) : (
                <div className="flex items-center gap-3">
                  <span className="text-sm text-[#475569]">Sign out everywhere?</span>
                  <button
                    type="button"
                    onClick={() => setConfirmRevokeAll(false)}
                    disabled={revoking !== null}
                    className="text-sm text-[#475569] hover:underline"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleRevokeAll}
                    disabled={revoking !== null}
                    className="text-sm font-semibold text-white bg-[#EF4444] hover:bg-[#DC2626] px-3 py-1.5 rounded-md disabled:opacity-50"
                  >
                    {revoking === "all" ? "Revoking…" : "Yes, revoke all"}
                  </button>
                </div>
              )}
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default SecuritySettings;
